import Button from '@carvana/ds-button';
import { CarvanaHorizontal } from '@carvana/ds-icons';
import { ChevronLeft, Menu } from 'lucide-react';
import LinkButton from '@carvana/ds-link-button';
import React, { useContext, useState } from 'react';
import { Summary, Dashboard } from '|/app/services';
import { MobileWebviewEvents } from '|/helpers/mobile-webview-events';
import { SupportContact, SupportMfeModal } from '|/components/support-contact';
import MobileNavigationRailContext from '|/context/mobile-navigation-rail-context';
import { canHandleBackNavigation } from '|/helpers/is-native-app';
import sendNativeEvent from '|/helpers/send-native-event';
import useAnalyticsContext from '|/hooks/use-analytics-context/use-analytics-context';
import usePurchaseHeaderEvent from '|/hooks/use-purchase-header-event';
import useScrollPosition from '|/hooks/use-scroll-position';
import styles from './purchase-header.module.css';
import { ExchangeBanner, ExchangeModal } from '../../exchange-banner';
import ImpersonationBanner from '../../impersonation-baner';
import { OutageBanner } from '../../outage-banner';
import Cart from '../components/cart';
import LockTimer, { LockTimerModal } from '../components/lock-timer';

interface PurchaseHeaderProps {
  isMobileNative: boolean;
  orderSummary?: Summary | null;
  dashboard?: Dashboard | null;
  isOrderSummaryModalOpen?: boolean;
  setIsOrderSummaryModalOpen?: (isOpen: boolean) => void;
}

const PurchaseHeader: React.FC<PurchaseHeaderProps> = ({
  isMobileNative = false,
  orderSummary = null,
  dashboard = null,
  isOrderSummaryModalOpen,
  setIsOrderSummaryModalOpen
}) => {
  const { setShowMobileNavigationRail } = useContext(MobileNavigationRailContext);
  const [isExchangeModalOpen, setIsExchangeModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isLockTimerModalOpen, setIsLockTimerModalOpen] = useState(false);
  const { trackEvent } = useAnalyticsContext();
  const { isAtTop } = useScrollPosition();

  usePurchaseHeaderEvent();

  const showNavigationMenu = () => {
    trackEvent('pp_clicked_list_icon');
    setShowMobileNavigationRail(value => !value);
  };

  const onLogoClick = () => {
    trackEvent('pp_clicked_logo_icon');
    window.location.href = '/';
  };

  const onLockTimerClick = () => {
    trackEvent('pp_clicked_lock_timer');
    setIsLockTimerModalOpen(true);
  };

  const saveAndExit = () => {
    sendNativeEvent({
      event: MobileWebviewEvents.SAVE_EXIT,
      payload: {},
      source: 'purchase'
    });
  };

  const handleBackButtonClick = () => {
    if (canHandleBackNavigation()) {
      sendNativeEvent({
        event: MobileWebviewEvents.WEBVIEW_BACK_TAPPED,
        payload: {},
        source: 'purchase'
      });
    } else if (window.history.length > 1) {
      window.history.back();
    } else {
      saveAndExit();
    }
  };

  return (
    <>
      <ImpersonationBanner />
      <OutageBanner />
      <header className={`${styles.header} ${!isAtTop ? styles.stickyHeader : ''}`}>
        {isMobileNative && (
          <div className={styles.nativeHeader}>
            <Button
              variant="tertiary"
              size="xs"
              icon={true}
              data-analytics-id="pp_header_back_button"
              className={styles.backButton}
              onClick={handleBackButtonClick}
            >
              <ChevronLeft color="var(--icon-inverse)" />
            </Button>
            <LinkButton
              variant="inverse"
              size="sm"
              underline={false}
              className={styles.saveExitButton}
              data-analytics-id="pp_header_save_exit_button"
              onClick={saveAndExit}
            >
              Save & exit
            </LinkButton>
          </div>
        )}
        <div className={styles.checkoutHeader}>
          {/* Desktop Logo */}
          {!isMobileNative && (
            <a
              href="/"
              className={styles.desktopLogo}
              onClick={e => {
                e.preventDefault();
                onLogoClick();
              }}
              data-analytics-id="pp_header_home"
            >
              <CarvanaHorizontal className={styles.carvanaLogo} />
            </a>
          )}

          {/* Right Content */}
          <div className={styles.rightContent}>
            {/* Desktop Support Button */}
            <div className={styles.desktopOnly}>
              <SupportContact
                onClick={() => {
                  setIsSupportModalOpen(true);
                }}
                data-analytics-id="pp-support-contact-button"
              />
            </div>

            <LockTimer
              orderSummary={orderSummary}
              onClick={onLockTimerClick}
              data-analytics-id="pp-lock-timer"
            />

            {/* Order Summary Cart */}
            <Cart
              orderSummary={orderSummary}
              dashboard={dashboard}
              isOrderSummaryModalOpen={isOrderSummaryModalOpen}
              setIsOrderSummaryModalOpen={setIsOrderSummaryModalOpen}
            />

            {/* Mobile Menu Icon */}
            <button
              className={styles.mobileMenuButton}
              onClick={showNavigationMenu}
              data-analytics-id="pp_header_mobile_list_icon"
              aria-label="Open navigation menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      <ExchangeBanner
        orderSummary={orderSummary}
        onSeeDetails={() => {
          setIsExchangeModalOpen(true);
        }}
      />

      {/* Exchange Modal */}
      <ExchangeModal
        isOpen={isExchangeModalOpen}
        orderSummary={orderSummary}
        onClose={() => {
          setIsExchangeModalOpen(false);
        }}
      />

      {/* Lock Timer Modal */}
      <LockTimerModal
        isOpen={isLockTimerModalOpen}
        onClose={() => {
          setIsLockTimerModalOpen(false);
        }}
      />

      {/* Support MFE Modal */}
      <SupportMfeModal
        isOpen={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false);
        }}
        closeMobileNavMenu={() => {
          setShowMobileNavigationRail(false);
        }}
      />
    </>
  );
};

export default PurchaseHeader;
